import type { JsonValue } from "../document/types.ts";
import { validateEntityType, type JsonSchema } from "./schema.ts";

/**
 * Object types (ADR-053): a game-declared behaviour a placed prop can switch on. A type names its states,
 * the collision policy of each, the state it starts in and the fields the author fills; the art is the
 * prop's, so any prop can be made interactive and keeps the frame the author chose. The editor and compiler
 * treat a type as shapes; what the states mean is the game's.
 */

/**
 * An object state's footprint: derived from the frame's art like a prop (`auto`), none, or explicit
 * `[column, rowsUp]` cells as the footprint editor writes them.
 */
export type ObjectFootprint = "auto" | "none" | { readonly cells: readonly (readonly [number, number])[] };

/**
 * One state of an object type. The frame the state shows comes from the placed prop (ADR-053): its own
 * frame for the initial state, `PropBehaviour.frames` for the rest.
 */
export interface ObjectState {
  readonly id: string;
  readonly footprint: ObjectFootprint;
}

/**
 * A verb that moves an object from one state to another (ADR-058), possibly the same one: `read` on a sign
 * is `standing` to `standing`. `requires` names the guards the game evaluates before applying it and
 * `plays` names the animation the transition shows; both are the game's vocabulary, carried as data and
 * given no meaning here. `reverse` shows that animation's strip from its last frame to its first (ADR-058's
 * amendment of 2026-10-03), so one strip serves a thing both ways: a well's bucket comes up by a strip and
 * goes down by the same strip reversed. It is present only when set and needs `plays`.
 */
export interface ObjectTransition {
  readonly verb: string;
  readonly from: string;
  readonly to: string;
  readonly requires?: readonly string[];
  readonly plays?: string;
  readonly reverse?: true;
}

/**
 * How a presence type is driven (ADR-058 amendment, P1.3-T4): `inside` is the state a prop shows while the
 * viewer's own character stands in its area, on that viewer's screen alone; it shows the initial state
 * otherwise. Such a prop holds no server state and is never lowered into an entity.
 */
export interface ObjectPresence {
  readonly inside: string;
}

/**
 * A mask of zone cells a type has the editor paint around a prop of it (ADR-096 decision 38): the zone type the
 * cells take, the mask's size in collision cells, its rows from the top as runs (`[first column, length]` from the
 * mask's left edge, one a row), and which of its cells lies on the cell the prop stands on (`footprintGroundCell`).
 * `activation` has the same command write the mask as the prop's activation cells. Data the editor gives no meaning
 * to beyond painting it: what the zone type does is the game's.
 */
export interface ZoneStampDefinition {
  readonly zoneType: string;
  readonly columns: number;
  readonly rows: number;
  readonly originColumn: number;
  readonly originRow: number;
  readonly runs: readonly (readonly [number, number])[];
  readonly activation?: true;
}

/**
 * An object type as the game registers it: its states, the one it starts in, the verbs that move it
 * between them, and the fields an author fills. A type carrying `presence` is driven by where the viewer's
 * own character stands rather than by verbs, so its transitions only name the strips each change plays. A
 * type declaring `mustBlock` (ADR-058's amendment of 2026-10-04) has every prop of it block at least one
 * collision cell in its initial state, or the map does not compile; it is present only when true. A type declaring
 * `uninterruptible` (ADR-058's amendment of 2026-10-07) has a thing of it take no verb while the strip of its last
 * change of state still plays; the game's server holds that order, Daedalus only carries the word, and it is present
 * only when true. A type declaring `zoneStamp` (ADR-096 decision 38) has the editor offer to paint that mask of zone
 * cells around a prop of it in one command; it is present only when the game declares one. A type declaring
 * `soundSetField` (ADR-058's amendment of 2026-10-13) names one of its own fields, a choice of strings, whose
 * value a compile writes to a prop's appearance as its sound set when it is an option other than the choice's
 * default; Daedalus reads nothing into the value. A type declaring `choiceLabels` gives the options of some of its
 * choices a label each, by field name and in the options' order, which the inspector shows in place of the options
 * and never stores. Both are type-level keys, so a contract reader built before them ignores them. A type declaring
 * `fieldGroups` (P1.8-T21) has the inspector draw some of its fields as folded groups with presets, with presets. A type declaring
 * `hiddenOnMap` (ADR-100's amendment of 2026-10-14, decision 57) has its things left out of the compiled chunks'
 * landmark lists, so a map picture never draws them; Daedalus reads nothing else into it, it is present only when
 * true, and a contract reader built before it ignores it.
 */
export interface ObjectTypeDefinition {
  readonly type: string;
  readonly label: string;
  readonly states: readonly ObjectState[];
  readonly initial: string;
  readonly presence?: ObjectPresence;
  readonly mustBlock?: true;
  readonly uninterruptible?: true;
  readonly zoneStamp?: ZoneStampDefinition;
  readonly soundSetField?: string;
  readonly hiddenOnMap?: true;
  readonly choiceLabels?: { readonly [field: string]: readonly string[] };
  readonly fieldGroups?: readonly FieldGroupDefinition[];
  readonly transitions: readonly ObjectTransition[];
  readonly schema: JsonSchema;
}

/**
 * A preset of a field group: a button that writes exactly these values to the group's fields in one undoable command.
 */
export interface FieldPreset {
  readonly id: string;
  readonly values: { readonly [field: string]: JsonValue };
}

/**
 * Some of a type's fields drawn together as one folded group in the inspector, with the presets that fill them (P1.8-T21):
 * the group's title, preset names and hint come from the game's words by `id`, and the group is shut while every one of
 * its fields holds its default or nothing. A type-level key of the contract, which a reader built before it ignores.
 */
export interface FieldGroupDefinition {
  readonly id: string;
  readonly fields: readonly string[];
  readonly presets?: readonly FieldPreset[];
}

/**
 * Where an object's frame sits, as a prop placement: the frame's columns centred on the point, its bottom
 * on the point's row.
 */
export interface ObjectPlacement {
  readonly x: number;
  readonly y: number;
  readonly flipX: false;
  readonly flipY: false;
  readonly sortPixels: 0;
}

/**
 * Checks an object type: at least one state, distinct state ids, an initial state among them, transitions
 * between declared states with at most one per verb and starting state, a presence declaration that fits
 * its states, and a field schema inside the supported subset.
 *
 * @param definition The object type.
 * @throws Error naming the type and the fault.
 */
export function validateObjectType(definition: ObjectTypeDefinition): void {
  if (definition.states.length === 0) {
    throw new Error(`object type ${definition.type} has no states`);
  }
  const ids = new Set<string>();
  for (const state of definition.states) {
    if (ids.has(state.id)) {
      throw new Error(`object type ${definition.type} defines state ${state.id} twice`);
    }
    ids.add(state.id);
  }
  if (!ids.has(definition.initial)) {
    throw new Error(`object type ${definition.type} has no initial state ${definition.initial}`);
  }
  validatePresence(definition, ids);
  validateTransitions(definition, ids);
  validateZoneStamp(definition);
  validateEntityType({ type: definition.type, label: definition.label, serverOnly: false, schema: definition.schema });
  if (definition.soundSetField !== undefined && soundSetChoice(definition) === undefined) {
    throw new Error(`object type ${definition.type} names the sound set field ${definition.soundSetField}, which its schema does not declare as a choice of strings`);
  }
  validateFieldGroups(definition);
  for (const [field, labels] of Object.entries(definition.choiceLabels ?? {})) {
    const property = definition.schema.properties[field];
    const options = property?.type === "string" ? property.enum : undefined;
    if (options === undefined || !Array.isArray(labels) || labels.length !== options.length || labels.some((label) => typeof label !== "string" || label.trim() === "")) {
      throw new Error(`object type ${definition.type} labels the choice ${field}, which needs one label, not blank, for each option of a choice its schema declares`);
    }
  }
}

/**
 * Checks an object type's field groups: each has a distinct id, names fields its schema declares and no other group
 * does, and each preset has a distinct id and writes only the group's own fields.
 *
 * @param definition The object type.
 * @throws Error naming the type and the fault.
 */
function validateFieldGroups(definition: ObjectTypeDefinition): void {
  const groups = new Set<string>();
  const taken = new Set<string>();
  for (const group of definition.fieldGroups ?? []) {
    if (group.id.trim() === "" || groups.has(group.id) || group.fields.length === 0) {
      throw new Error(`object type ${definition.type} has a field group with a blank or repeated id or no fields`);
    }
    groups.add(group.id);
    for (const field of group.fields) {
      if (definition.schema.properties[field] === undefined || taken.has(field)) {
        throw new Error(`object type ${definition.type} puts ${field} in the field group ${group.id}, which its schema does not declare or another group holds`);
      }
      taken.add(field);
    }
    const presets = new Set<string>();
    for (const preset of group.presets ?? []) {
      if (preset.id.trim() === "" || presets.has(preset.id) || Object.keys(preset.values).some((field) => !group.fields.includes(field))) {
        throw new Error(`object type ${definition.type} has a preset of the field group ${group.id} with a blank or repeated id or a field the group lacks`);
      }
      presets.add(preset.id);
    }
  }
}

/**
 * The choice an object type names as its sound set field (ADR-058's amendment of 2026-10-13): the field's name,
 * its options and its default.
 *
 * @param definition The object type.
 * @returns The choice, or undefined when the type names no field or names one its schema does not declare as a
 *   choice of strings.
 */
export function soundSetChoice(definition: Pick<ObjectTypeDefinition, "soundSetField" | "schema">): { readonly field: string; readonly options: readonly string[]; readonly default?: string } | undefined {
  const field = definition.soundSetField;
  const property = field === undefined ? undefined : definition.schema.properties[field];
  if (field === undefined || property === undefined || property.type !== "string" || property.enum === undefined) {
    return undefined;
  }
  return { field, options: [...property.enum], ...(property.default === undefined ? {} : { default: property.default }) };
}

/**
 * The most cells a zone stamp's mask may span along either axis: a stamp is a small area around one prop, and its
 * cells are written into the prop's behaviour.
 */
export const MAX_ZONE_STAMP_SPAN = 64;

/**
 * Checks a type's zone stamp, when it declares one: a zone type id that is not blank, a mask of whole positive
 * columns and rows no wider than `MAX_ZONE_STAMP_SPAN`, one run a row, each a whole first column and a whole positive
 * length inside the mask's columns, and an origin that is a cell of the mask's box. Whether the game registers the
 * zone type is `registerAdapter`'s to check, since a type alone does not know the zone types.
 *
 * @param definition The object type.
 * @throws Error naming the type and the fault.
 */
function validateZoneStamp(definition: ObjectTypeDefinition): void {
  const stamp = definition.zoneStamp;
  if (stamp === undefined) {
    return;
  }
  const name = `object type ${definition.type}'s zone stamp`;
  if (typeof stamp.zoneType !== "string" || stamp.zoneType === "") {
    throw new Error(`${name} names no zone type`);
  }
  for (const [key, value] of [["columns", stamp.columns], ["rows", stamp.rows]] as const) {
    if (!Number.isInteger(value) || value < 1 || value > MAX_ZONE_STAMP_SPAN) {
      throw new Error(`${name} has ${key} ${value}, not a whole number from 1 to ${MAX_ZONE_STAMP_SPAN}`);
    }
  }
  if (stamp.runs.length !== stamp.rows) {
    throw new Error(`${name} declares ${stamp.rows} rows and ${stamp.runs.length} runs; give one run a row`);
  }
  stamp.runs.forEach((run, row) => {
    const [first, length] = run;
    if (run.length !== 2 || !Number.isInteger(first) || !Number.isInteger(length) || first < 0 || length < 1 || first + length > stamp.columns) {
      throw new Error(`${name} has a run on row ${row} that is not a first column and a length inside its ${stamp.columns} columns`);
    }
  });
  if (!Number.isInteger(stamp.originColumn) || !Number.isInteger(stamp.originRow) || stamp.originColumn < 0 || stamp.originRow < 0 || stamp.originColumn >= stamp.columns || stamp.originRow >= stamp.rows) {
    throw new Error(`${name} has its origin at column ${stamp.originColumn}, row ${stamp.originRow}, outside its ${stamp.columns} by ${stamp.rows} cells`);
  }
}

/**
 * Checks that every zone stamp paints a zone type the game registers, which a type alone cannot know.
 *
 * @param definitions The game's object types.
 * @param zoneTypes The ids of the zone types the game registers.
 * @throws Error naming the type and the zone type.
 */
export function assertZoneStampTypes(definitions: readonly ObjectTypeDefinition[], zoneTypes: readonly string[]): void {
  for (const definition of definitions) {
    const stamped = definition.zoneStamp?.zoneType;
    if (stamped !== undefined && !zoneTypes.includes(stamped)) {
      throw new Error(`object type ${definition.type}'s zone stamp paints zone type ${stamped}, which the game does not register`);
    }
  }
}

/**
 * Checks a presence declaration (ADR-058 amendment, P1.3-T4): the type has exactly its initial state and
 * the inside state, and neither blocks, since collision is shared by the map server and every client and a state
 * only one viewer sees cannot move it.
 *
 * @param definition The object type.
 * @param states Its state ids.
 * @throws Error naming the type and the fault.
 */
function validatePresence(definition: ObjectTypeDefinition, states: ReadonlySet<string>): void {
  const presence = definition.presence;
  if (presence === undefined) {
    return;
  }
  const name = `object type ${definition.type}`;
  if (!states.has(presence.inside)) {
    throw new Error(`${name} names inside state ${presence.inside}, which is not one of its states`);
  }
  if (presence.inside === definition.initial) {
    throw new Error(`${name} names inside state ${presence.inside}, which is its initial state`);
  }
  if (definition.states.length !== 2) {
    throw new Error(`${name} is driven by presence, so it has 2 states, not ${definition.states.length}`);
  }
  for (const state of definition.states) {
    if (state.footprint !== "none") {
      throw new Error(`${name} is driven by presence, so its state ${state.id} blocks nothing: declare footprint none`);
    }
  }
}

/**
 * Checks an object type's transitions against its states. Two transitions sharing a verb and a starting
 * state would leave the state the verb reaches ambiguous, so the pair is unique; the same verb from
 * different states is allowed. A transition marked `reverse` names the animation it reverses.
 *
 * @param definition The object type.
 * @param states Its state ids.
 * @throws Error naming the type, the verb and the fault.
 */
function validateTransitions(definition: ObjectTypeDefinition, states: ReadonlySet<string>): void {
  const seen = new Set<string>();
  for (const transition of definition.transitions) {
    const name = `object type ${definition.type} transition ${transition.verb}`;
    if (transition.verb.length === 0) {
      throw new Error(`object type ${definition.type} has a transition with an empty verb`);
    }
    if (!states.has(transition.from)) {
      throw new Error(`${name} starts from ${transition.from}, which is not one of its states`);
    }
    if (!states.has(transition.to)) {
      throw new Error(`${name} goes to ${transition.to}, which is not one of its states`);
    }
    const key = `${transition.verb}\u0000${transition.from}`;
    if (seen.has(key)) {
      throw new Error(`${name} leaves from ${transition.from} twice`);
    }
    seen.add(key);
    if (transition.requires !== undefined && transition.requires.some((guard) => guard.length === 0)) {
      throw new Error(`${name} requires an empty guard name`);
    }
    if (transition.plays !== undefined && transition.plays.length === 0) {
      throw new Error(`${name} plays an empty animation name`);
    }
    if (transition.reverse === true && transition.plays === undefined) {
      throw new Error(`${name} is marked reverse but plays no animation to reverse`);
    }
  }
}

/**
 * The prop placement of a frame standing on a point: columns centred on the point, bottom on the point's
 * row.
 *
 * @param point The feet point in tiles.
 * @param frameWidth Frame width in pixels.
 * @param tileSize Tile size in pixels.
 * @returns The placement.
 */
export function objectAnchor(point: { readonly x: number; readonly y: number }, frameWidth: number, tileSize: number): ObjectPlacement {
  return { x: point.x - frameWidth / (2 * tileSize), y: point.y - 1, flipX: false, flipY: false, sortPixels: 0 };
}

/**
 * The feet point of a placed frame: the bottom centre of its art, the point the map server, the activation cells
 * and the chunk assignment use (ADR-053 decision 6). Inverse of `objectAnchor`.
 *
 * @param placement Where the frame's top-left sits, in tiles.
 * @param frameWidth Frame width in pixels.
 * @param tileSize Tile size in pixels.
 * @returns The feet point in tiles.
 */
export function propFeet(placement: { readonly x: number; readonly y: number }, frameWidth: number, tileSize: number): { readonly x: number; readonly y: number } {
  return { x: placement.x + frameWidth / (2 * tileSize), y: placement.y + 1 };
}
